import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  CheckboxDropdown,
  DataState,
  EmptyState,
  PageHeader,
  SearchInput,
  Select,
  StatusBadge,
  Table,
  formatDate,
  type Column,
} from "@vms/ui-web";
import { GAME_LABELS } from "@vms/types";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface EventRow {
  id: string;
  name: string;
  mode: "hotlap" | "race";
  status: "draft" | "active" | "completed" | "deleted" | null;
  track: string | null;
  trackName?: string | null;
  game: string | null;
  startDate?: string | null;
  endDate?: string | null;
  scheduledFor: string | null;
  simulatorAllocation?: number | null;
  numberGuests?: number | null;
  createdAt: string;
}

type DateFilter = "today-and-active" | "tomorrow" | "this-week" | "upcoming" | "all" | "completed" | "custom";
type SortKey = string;
type SortDir = "asc" | "desc";

const DATE_OPTIONS = [
  { value: "today-and-active", label: "Today & Active" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this-week", label: "This Week" },
  { value: "upcoming", label: "All Upcoming" },
  { value: "all", label: "All Events" },
  { value: "completed", label: "Completed" },
  { value: "custom", label: "Custom Range" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "deleted", label: "Deleted" },
];

const GAME_OPTIONS = [
  { value: "assetto-corsa", label: "Assetto Corsa" },
  { value: "rfactor-2", label: "rFactor 2" },
];

const MODE_OPTIONS = [
  { value: "hotlap", label: "Hotlap" },
  { value: "race", label: "Race" },
];

const STATUS_PRIORITY: Record<string, number> = { active: 0, draft: 1, completed: 2, deleted: 3 };

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function endOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(23, 59, 59, 999);
  return r;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function toYMD(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function labelForGame(game: string | null | undefined): string {
  if (!game) return "";
  return (GAME_LABELS as Record<string, string>)[game] ?? game;
}

function getSortValue(e: EventRow, key: string): string | number {
  switch (key) {
    case "name": return e.name.toLowerCase();
    case "status": return STATUS_PRIORITY[e.status ?? "draft"] ?? 1;
    case "mode": return e.mode;
    case "game": return labelForGame(e.game) || "";
    case "track": return (e.trackName ?? e.track ?? "").toLowerCase();
    case "startDate":
      return e.startDate ? new Date(e.startDate).getTime() : new Date(e.createdAt).getTime();
    case "endDate":
      return e.endDate ? new Date(e.endDate).getTime() : new Date(e.createdAt).getTime();
    default: return "";
  }
}

export function VenueEvents() {
  const { currentOrg, currentVenue } = useOrgContext();
  const events = useQuery<EventRow[]>(
    currentVenue ? `/api/venues/${currentVenue.id}/events` : null,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [dateFilter, setDateFilter] = useState<DateFilter>("today-and-active");
  const [customFrom, setCustomFrom] = useState(() => toYMD(new Date()));
  const [customTo, setCustomTo] = useState(() => toYMD(new Date()));
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [gameFilters, setGameFilters] = useState<string[]>([]);
  const [modeFilters, setModeFilters] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const rows = events.data ?? [];

  const isFiltered =
    dateFilter !== "today-and-active" ||
    statusFilters.length > 0 ||
    gameFilters.length > 0 ||
    modeFilters.length > 0 ||
    searchQuery !== "";

  function clearFilters() {
    setDateFilter("today-and-active");
    setStatusFilters([]);
    setGameFilters([]);
    setModeFilters([]);
    setSearchQuery("");
    setSearchResetKey((k) => k + 1);
  }

  function handleSort(key: string) {
    if (sortKey === key) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortKey("");
        setSortDir("asc");
      }
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const filtered = useMemo(() => {
    const now = new Date();
    const todayS = startOfDay(now);
    const todayE = endOfDay(now);

    function eventOverlaps(e: EventRow, windowStart: Date, windowEnd: Date): boolean {
      if (!e.startDate) return false;
      const eStart = new Date(e.startDate);
      const eEnd = e.endDate ? new Date(e.endDate) : eStart;
      return eStart <= windowEnd && eEnd >= windowStart;
    }

    let result = rows.filter((e) => {
      switch (dateFilter) {
        case "today-and-active":
          return e.status === "active" || eventOverlaps(e, todayS, todayE);
        case "tomorrow": {
          const tmrS = startOfDay(addDays(now, 1));
          const tmrE = endOfDay(addDays(now, 1));
          return eventOverlaps(e, tmrS, tmrE);
        }
        case "this-week": {
          const weekE = endOfDay(addDays(now, 6));
          return eventOverlaps(e, todayS, weekE);
        }
        case "upcoming":
          return e.status === "draft" && e.startDate != null && new Date(e.startDate) >= todayS;
        case "completed":
          return e.status === "completed";
        case "custom": {
          const from = startOfDay(new Date(customFrom));
          const to = endOfDay(new Date(customTo));
          return eventOverlaps(e, from, to);
        }
        case "all":
        default:
          return true;
      }
    });

    if (statusFilters.length > 0 && dateFilter !== "completed") {
      result = result.filter((e) => statusFilters.includes(e.status ?? "draft"));
    }
    if (gameFilters.length > 0) {
      result = result.filter((e) => gameFilters.includes(e.game ?? ""));
    }
    if (modeFilters.length > 0) {
      result = result.filter((e) => modeFilters.includes(e.mode));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((e) => e.name.toLowerCase().includes(q));
    }

    if (sortKey) {
      result.sort((a, b) => {
        const av = getSortValue(a, sortKey);
        const bv = getSortValue(b, sortKey);
        let cmp = 0;
        if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
        else cmp = String(av).localeCompare(String(bv));
        return sortDir === "desc" ? -cmp : cmp;
      });
    } else {
      result.sort((a, b) => {
        const pa = STATUS_PRIORITY[a.status ?? "draft"] ?? 1;
        const pb = STATUS_PRIORITY[b.status ?? "draft"] ?? 1;
        if (pa !== pb) return pa - pb;
        const aTime = a.startDate ? new Date(a.startDate).getTime() : new Date(a.createdAt).getTime();
        const bTime = b.startDate ? new Date(b.startDate).getTime() : new Date(b.createdAt).getTime();
        if ((a.status ?? "draft") === "completed") return bTime - aTime;
        return aTime - bTime;
      });
    }

    return result;
  }, [rows, dateFilter, customFrom, customTo, statusFilters, gameFilters, modeFilters, searchQuery, sortKey, sortDir]);

  const eventBase =
    currentOrg && currentVenue
      ? `/org/${currentOrg.slug}/venue/${currentVenue.slug}/events`
      : "";

  const columns: Column<EventRow>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (e) => (
        <Link
          to={`${eventBase}/${e.id}`}
          className="font-medium text-indigo-600 hover:text-indigo-800"
        >
          {e.name}
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (e) => <StatusBadge domain="event" status={e.status ?? "draft"} />,
    },
    {
      key: "mode",
      header: "Mode",
      sortable: true,
      render: (e) => <StatusBadge domain="mode" status={e.mode} />,
    },
    {
      key: "startDate",
      header: "Start",
      sortable: true,
      render: (e) => formatDate(e.startDate),
    },
    {
      key: "endDate",
      header: "End",
      sortable: true,
      render: (e) => formatDate(e.endDate),
    },
    {
      key: "game",
      header: "Game",
      sortable: true,
      render: (e) => labelForGame(e.game) || "—",
    },
    {
      key: "track",
      header: "Track",
      sortable: true,
      render: (e) => e.trackName ?? e.track ?? "—",
    },
  ];

  if (!currentVenue) return null;

  const resultCountText =
    isFiltered && rows.length > 0
      ? `${filtered.length} of ${rows.length} events`
      : `${rows.length} events`;

  return (
    <div>
      <PageHeader
        title="Events"
        subtitle="Pre-scheduled events at this venue. Once active or completed, edits happen on the hub."
      />

      <DataState
        loading={events.loading}
        error={events.error}
        empty={!events.loading && rows.length === 0}
        emptyMessage="No events yet."
      >
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="w-56">
              <SearchInput
                key={searchResetKey}
                onSearch={setSearchQuery}
                placeholder="Search events..."
              />
            </div>
            <div className="w-40">
              <Select
                options={DATE_OPTIONS}
                value={dateFilter}
                onChange={(ev) => setDateFilter(ev.target.value as DateFilter)}
              />
            </div>
            <CheckboxDropdown
              label="Status"
              options={STATUS_OPTIONS}
              selected={statusFilters}
              onChange={setStatusFilters}
            />
            <CheckboxDropdown
              label="Game"
              options={GAME_OPTIONS}
              selected={gameFilters}
              onChange={setGameFilters}
            />
            <CheckboxDropdown
              label="Mode"
              options={MODE_OPTIONS}
              selected={modeFilters}
              onChange={setModeFilters}
            />
            {isFiltered && (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>

          {dateFilter === "custom" && (
            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm text-gray-600">
                From:
                <input
                  type="date"
                  value={customFrom}
                  onChange={(ev) => setCustomFrom(ev.target.value)}
                  className="ml-2 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </label>
              <label className="text-sm text-gray-600">
                To:
                <input
                  type="date"
                  value={customTo}
                  onChange={(ev) => setCustomTo(ev.target.value)}
                  className="ml-2 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </label>
            </div>
          )}

          <p className="text-sm text-gray-500 mb-3">{resultCountText}</p>

          {filtered.length === 0 ? (
            <EmptyState
              message="No events match your filters."
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  Clear Filters
                </Button>
              }
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <Table
                columns={columns}
                data={filtered}
                keyExtractor={(e) => e.id}
                sortKey={sortKey || undefined}
                sortDirection={sortDir}
                onSort={handleSort}
                rowClassName={(e) => (e.status === "deleted" ? "bg-red-50" : "")}
              />
            </div>
          )}
        </>
      </DataState>
    </div>
  );
}
